import * as Colors from '@wandb/weave/common/css/color.styles';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  FlameGraph,
  FlameGraphNode as FlameGraphNodeType,
} from 'react-flame-graph';

import {parseSpanName} from '../../../pages/wfReactInterface/tsDataModelHooks';
import TraceScrubber from '../TraceScrubber';
import {TraceViewProps} from './types';
import {getCallDisplayName, getColorForOpName} from './utils';
// Use the imported type directly
type FlameGraphNode = FlameGraphNodeType;

// Helper function to format duration
const formatDuration = (ms: number): string => {
  if (ms < 1) {
    return `${(ms * 1000).toFixed(2)}μs`;
  } else if (ms < 1000) {
    return `${ms.toFixed(2)}ms`;
  } else if (ms < 60000) {
    return `${(ms / 1000).toFixed(2)}s`;
  } else {
    const minutes = Math.floor(ms / 60000);
    const seconds = ((ms % 60000) / 1000).toFixed(2);
    return `${minutes}m ${seconds}s`;
  }
};

// Helper function to format timestamp
const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString();
};

export const FlameGraphView: React.FC<TraceViewProps> = props => {
  const {
    traceTreeFlat,
    focusedCallId: selectedCallId,
    setFocusedCallId: onCallSelect,
  } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({width: 0, height: 0});
  const [tooltip, setTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    node: FlameGraphNode | null;
  }>({
    visible: false,
    x: 0,
    y: 0,
    node: null,
  });

  // Update dimensions when container size changes
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const {width, height} = containerRef.current.getBoundingClientRect();
        setDimensions({
          width: Math.floor(width),
          height: Math.floor(height - 16), // Account for padding
        });
      }
    };

    // Initial measurement
    updateDimensions();

    // Set up resize observer
    const observer = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleMouseMove = useCallback(
    (event: React.MouseEvent, node: FlameGraphNode) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setTooltip(prev => ({
          visible: true,
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
          node,
        }));
      }
    },
    []
  );

  const handleMouseOut = useCallback(() => {
    setTooltip(prev => ({...prev, visible: false}));
  }, []);

  const flameData = useMemo(() => {
    // Find the root node
    const rootNode = Object.values(traceTreeFlat).find(
      node => !node.parentId || !traceTreeFlat[node.parentId]
    );
    if (!rootNode) {
      return null;
    }

    // Calculate total trace duration for scaling
    const traceStartTime = Date.parse(rootNode.call.started_at);
    const traceEndTime = Object.values(traceTreeFlat).reduce((maxEnd, node) => {
      const endTime = node.call.ended_at
        ? Date.parse(node.call.ended_at)
        : Date.now();
      return Math.max(maxEnd, endTime);
    }, traceStartTime);
    const totalDuration = traceEndTime - traceStartTime;

    // Minimum width as a percentage of total duration (1%)
    const MIN_WIDTH_PERCENT = 0.01;
    const minDuration = totalDuration * MIN_WIDTH_PERCENT;

    // Helper function to build the flame graph tree and return total duration
    const buildFlameNode = (nodeId: string): [FlameGraphNode, number] => {
      const node = traceTreeFlat[nodeId];
      const call = node.call;
      const startTime = Date.parse(call.started_at);
      const endTime = call.ended_at ? Date.parse(call.ended_at) : Date.now();
      let duration = endTime - startTime;

      // First, build children and get their total adjusted duration
      const childResults = node.childrenIds.map(childId =>
        buildFlameNode(childId)
      );
      const children = childResults.map(([innerNode]) => innerNode);
      const childrenTotalDuration = childResults.reduce(
        (sum, [_, dur]) => sum + dur,
        0
      );

      // Ensure our duration is at least the minimum and can contain our children
      duration = Math.max(duration, minDuration, childrenTotalDuration);

      const opNameReal = parseSpanName(call.op_name);
      const isSelected = nodeId === selectedCallId;

      return [
        {
          id: nodeId,
          name: getCallDisplayName(call),
          value: duration,
          children: children.length > 0 ? children : undefined,
          backgroundColor: isSelected
            ? Colors.TEAL_500
            : getColorForOpName(opNameReal),
          color: isSelected ? Colors.WHITE : undefined,
          timing: {
            start: startTime,
            end: endTime,
          },
        },
        duration,
      ];
    };

    return buildFlameNode(rootNode.id)[0];
  }, [traceTreeFlat, selectedCallId]);

  if (!flameData) {
    return (
      <div className="flex h-full items-center justify-center text-moon-500">
        No data available for flame graph
      </div>
    );
  }

  const TooltipContent = (
    <>
      {tooltip.visible && tooltip.node && tooltip.node.timing && (
        <div
          style={{
            position: 'absolute',
            right: `calc(100% - ${tooltip.x}px + 10px)`,
            top: tooltip.y + 10,
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '12px',
            maxWidth: '300px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
            pointerEvents: 'none',
            zIndex: 1000,
          }}>
          <div style={{fontWeight: 'bold', marginBottom: '6px'}}>
            {tooltip.node.name}
          </div>
          <div style={{marginBottom: '2px'}}>
            <span style={{color: '#aaa'}}>Duration: </span>
            {formatDuration(
              tooltip.node.timing.end - tooltip.node.timing.start
            )}
          </div>
          <div style={{marginBottom: '2px'}}>
            <span style={{color: '#aaa'}}>Start: </span>
            {formatTimestamp(tooltip.node.timing.start)}
          </div>
          <div>
            <span style={{color: '#aaa'}}>End: </span>
            {formatTimestamp(tooltip.node.timing.end)}
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="flex h-full flex-col">
      <div
        ref={containerRef}
        className="h-[100%] w-full flex-1 overflow-hidden p-4">
        {dimensions.width > 0 && dimensions.height > 0 && (
          <FlameGraph
            data={flameData}
            height={dimensions.height}
            width={dimensions.width}
            onChange={(node: {source: FlameGraphNodeType}) => {
              onCallSelect(node.source.id);
            }}
            disableDefaultTooltips={true}
            onMouseOver={handleMouseMove}
            onMouseMove={handleMouseMove}
            onMouseOut={handleMouseOut}
          />
        )}
        {TooltipContent}
      </div>
      <div className="flex-0">
        <TraceScrubber
          {...props}
          allowedScrubbers={['timeline', 'peer', 'sibling', 'stack']}
        />
      </div>
    </div>
  );
};
